/** biome-ignore-all lint/suspicious/noAssignInExpressions: <3rd-party library> */
/** biome-ignore-all lint/suspicious/noImplicitAnyLet: <3rd-party library> */
import { type PDFDocumentProxy } from 'pdfjs-dist';

import { type PDFPagesCache } from '../../../utils/PDFPagesCache';
import { getOriginalIndex } from './utils/getOriginalIndex';
import { DIACRITICS_EXCEPTION, normalizePDFText, SPECIAL_CHARS_REG_EXP } from './utils/normalizePDFText';

const textOptions = { disableNormalization: true };

type SearchResultsResponse = {
  totalResults: number;
  pageMatchesLength: number[][];
  pageMatches: number[][];
};

/**
 * This class is responsible for fiding text in the PDF.
 * It was inspired by pdf.js/web/pdf_find_controller.js
 */
export class PDFFindController {
  private readonly pdfDocument: PDFDocumentProxy;
  private readonly pdfPagesCache: PDFPagesCache | undefined;

  private extractTextPromises: Promise<void>[] = [];
  private pendingFindMatches = new Set<number>();

  private pageContents: string[] = []; // Stores the normalized text for each page.
  private pageDiffs: Array<number[][] | null> = [];
  private hasDiacritics: boolean[] = [];

  private _pageMatches: number[][] = [];
  private _pageMatchesLength: number[][] = [];
  private matchesCountTotal = 0;

  constructor(pdfDocument: PDFDocumentProxy, pdfPagesCache: PDFPagesCache | undefined) {
    this.pdfDocument = pdfDocument;
    this.pdfPagesCache = pdfPagesCache;
  }

  get pageMatches() {
    return this._pageMatches;
  }

  get pageMatchesLength() {
    return this._pageMatchesLength;
  }

  private prepareExtractTextFromPagesPromises = () => {
    if (this.extractTextPromises.length > 0) {
      return;
    }

    for (let pageIndex = 0; pageIndex < this.pdfDocument.numPages; pageIndex++) {
      this.extractTextPromises[pageIndex] = new Promise((resolve) => {
        this.pdfPagesCache
          ?.getPage(pageIndex + 1)
          .then((page) => page?.getTextContent(textOptions))
          .then((textContent) => {
            const strBuf = [];

            if (!textContent) {
              return;
            }

            for (const textItem of textContent.items) {
              if ('str' in textItem) {
                strBuf.push(textItem.str);
                if (textItem.hasEOL) {
                  strBuf.push('\n');
                }
              }
            }

            const { normalized, hasDiacritics, positions } = normalizePDFText(strBuf.join(''));
            this.pageContents[pageIndex] = normalized;
            this.pageDiffs[pageIndex] = positions;
            this.hasDiacritics[pageIndex] = hasDiacritics;

            resolve();
          })
          .catch((error) => {
            // Page error -- assuming no text content.
            this.pageContents[pageIndex] = '';
            this.pageDiffs[pageIndex] = null;
            this.hasDiacritics[pageIndex] = false;

            console.error(`Unable to get text content for page ${pageIndex + 1}`, error);
            resolve();
          });
      });
    }
  };

  private convertToRegExpString = (query: string, hasDiacritics: boolean) => {
    let isUnicode = false;
    query = query.replaceAll(SPECIAL_CHARS_REG_EXP, (_, toEscape, punctuation, whitespaces, diacritics, letters) => {
      // We don't need to use a \s for whitespaces since all the different
      // kind of whitespaces are replaced by a single " ".

      if (toEscape) {
        // Escape characters like *+?... to not interfer with regexp syntax.
        return `[ ]*\\${toEscape}[ ]*`;
      }
      if (punctuation) {
        // Allow whitespaces around punctuation signs.
        return `[ ]*${punctuation}[ ]*`;
      }
      if (whitespaces) {
        // Replace spaces by \s+ to be sure to match any spaces.
        return '[ ]+';
      }

      if (diacritics) {
        // Diacritics are removed with few exceptions.
        return DIACRITICS_EXCEPTION.has(diacritics.charCodeAt(0)) ? diacritics : '';
      }

      // A letter has been matched and it can be followed by any diacritics
      // in normalized text.
      if (hasDiacritics) {
        isUnicode = true;
        return `${letters}\\p{M}*`;
      }
      return letters;
    });

    const trailingSpaces = '[ ]*';
    if (query.endsWith(trailingSpaces)) {
      // The [ ]* has been added in order to help to match "foo . bar" but
      // it doesn't make sense to match some whitespaces after the dot
      // when it's the last character.
      query = query.slice(0, query.length - trailingSpaces.length);
    }

    return {
      isUnicode,
      query,
    };
  };

  private createPageMatchesForQuery = (query: RegExp, pageIndex: number) => {
    const pageContent = this.pageContents[pageIndex];
    this._pageMatches[pageIndex] = [];
    this._pageMatchesLength[pageIndex] = [];
    if (!query) {
      // The query can be empty because some chars like diacritics could have
      // been stripped out.
      return;
    }
    let match;
    while ((match = query.exec(pageContent)) !== null) {
      const [matchPos, matchLen] = getOriginalIndex(this.pageDiffs[pageIndex], match.index, match[0].length);

      if (matchLen) {
        this._pageMatches[pageIndex].push(matchPos);
        this._pageMatchesLength[pageIndex].push(matchLen);
      }
    }
  };

  private findPhraseOnPage = (pageIndex: number, phrase: string) => {
    const hasDiacritics = this.hasDiacritics[pageIndex];

    const { query, isUnicode } = this.convertToRegExpString(phrase, hasDiacritics);
    const flags = `g${isUnicode ? 'u' : ''}i`;
    const phraseRegExp = new RegExp(query, flags);

    this.createPageMatchesForQuery(phraseRegExp, pageIndex);

    const pageMatchesCount = this._pageMatches[pageIndex].length;
    this.matchesCountTotal += pageMatchesCount;
  };

  searchInPdf = async (phrase: string): Promise<SearchResultsResponse> => {
    if (phrase.length === 0) {
      return {
        pageMatches: [],
        pageMatchesLength: [],
        totalResults: 0,
      };
    }

    this.prepareExtractTextFromPagesPromises();

    const pageNumbersArray = Array(this.pdfDocument.numPages).fill(0);
    await Promise.all(pageNumbersArray.map((_, pageIndex) => this.extractTextPromises[pageIndex]));

    pageNumbersArray.forEach((_, pageIndex) => {
      this.findPhraseOnPage(pageIndex, phrase);
    });

    return {
      pageMatches: this._pageMatches,
      pageMatchesLength: this._pageMatchesLength,
      totalResults: this.matchesCountTotal,
    };
  };

  resetSearch = () => {
    this._pageMatches.length = 0;
    this._pageMatchesLength.length = 0;
    this.matchesCountTotal = 0;
    this.pendingFindMatches.clear();
  };

  cleanup = () => {
    this.resetSearch();
    this.pageContents.length = 0;
    this.pageDiffs.length = 0;
    this.hasDiacritics.length = 0;
    this.extractTextPromises.length = 0;
  };
}
